/**
 * ``useAnnotationMeasurementHover`` — show an annotation's measurement readout in
 * a hover **tooltip** when its ``measurement_display`` is ``"hover"`` (ADR-034
 * #8). The operator sets the mode per-shape in the Entity Details panel; in
 * ``hover`` mode the measurement is omitted from the always-on map label
 * (annotation-label.ts) and shown here instead — a MapLibre ``Popup`` at the
 * cursor, re-skinned to the operator-tooling register (annotation-tooltip.css).
 * The operator's text label stays on the map regardless (per the #8 decision —
 * only the measurement readout is hover-gated).
 *
 * On every ``mousemove`` it finds the annotation under the cursor and, if that
 * annotation is in hover mode (with measurements on), shows the readout formatted
 * per the operator's live units; otherwise it hides the tooltip. No tooltip while
 * a draw tool is active (the operator is drawing, not inspecting).
 *
 * Renders nothing. WebGL-only — verified in the §5.11 visual pass; the pure
 * readout it shows (:func:`annotationHoverMeasurementText`) is unit-tested.
 *
 * Cross-references:
 *   - ADR-034 (#8 — measurement hover mode); feature-spec §7.
 *   - @/annotations/annotation-label (annotationHoverMeasurementText).
 *   - @/map/use-annotation-drag (the sibling map-interaction hook this mirrors).
 */
import { useEffect, useRef } from "react";
import { Popup, type Map as MapLibreMap, type MapMouseEvent } from "maplibre-gl";
import { isMapUsable } from "@/map/map-ready";
import { useAnnotations } from "@/annotations/use-annotations";
import { usePreferences } from "@/preferences/preferences-context";
import { useAnnotationToolStore } from "@/annotations/annotation-tool-store";
import { annotationHoverMeasurementText } from "@/annotations/annotation-label";

/** Mount the annotation measurement hover tooltip. Call once with the loaded map
 *  (``null`` before load / after teardown — a no-op until then). */
export function useAnnotationMeasurementHover(map: MapLibreMap | null): void {
  const annotationsQuery = useAnnotations();
  const units = usePreferences().workingState.units;
  const distanceUnit = units?.distance ?? "nautical_miles";
  const coordinateFormat = units?.coordinate_format ?? "decimal_degrees";

  // Latest-refs so the once-registered handler always reads the current
  // annotations + units (both change independently of the map lifecycle).
  const annotationsRef = useRef(annotationsQuery.data);
  const unitsRef = useRef({ distanceUnit, coordinateFormat });
  useEffect(() => {
    annotationsRef.current = annotationsQuery.data;
  });
  useEffect(() => {
    unitsRef.current = { distanceUnit, coordinateFormat };
  });

  useEffect(() => {
    if (map === null || !isMapUsable(map)) {
      return undefined;
    }
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
      className: "annotation-measurement-tooltip",
      offset: 12,
    });
    let shown = false;
    const hide = (): void => {
      if (shown) {
        popup.remove();
        shown = false;
      }
    };

    const onMouseMove = (event: MapMouseEvent): void => {
      // Never show while a draw tool is active — the operator is drawing.
      if (useAnnotationToolStore.getState().activeTool !== null) {
        hide();
        return;
      }
      const annotations = annotationsRef.current;
      if (annotations === undefined) {
        hide();
        return;
      }
      const hit = map
        .queryRenderedFeatures(event.point)
        .find((feature) => typeof feature.properties?.annotation_id === "string");
      const id = hit?.properties?.annotation_id as string | undefined;
      const annotation =
        id !== undefined ? annotations.find((a) => a.annotation_id === id) : undefined;
      if (annotation === undefined) {
        hide();
        return;
      }
      const { distanceUnit: du, coordinateFormat: cf } = unitsRef.current;
      const text = annotationHoverMeasurementText(annotation, du, cf);
      if (text === null) {
        hide();
        return;
      }
      // ``setText`` (not setHTML) — the readout is escaped; the newline-joined
      // measurements render multi-line via the tooltip's white-space: pre-line.
      popup.setLngLat(event.lngLat).setText(text).addTo(map);
      shown = true;
    };

    map.on("mousemove", onMouseMove);
    map.on("mouseout", hide);

    return () => {
      if (isMapUsable(map)) {
        map.off("mousemove", onMouseMove);
        map.off("mouseout", hide);
      }
      popup.remove();
    };
  }, [map]);
}
